/**
 * Baustein — Select
 * Einzelauswahl-Feld in Master-Optik: Trigger-Button in Feld-Optik (analog ui/Input)
 * + Radix-Dropdown-Menü mit RadioItems — Trigger UND aufgeklapptes Menü tragen
 * Design-System-Optik (Fundament wie FilterStrip.PillMenu, Menü-Optik aus ui/menu.css).
 *
 *   <Select value={v} onChange={setV}
 *           options={[{ value: 'a', label: 'Alpha' }]} placeholder="Wählen…" />
 *
 * onChange liefert den WERT direkt (kein Event).
 * portal={false} für den Einsatz IN einem Radix-Popover (z.B. künftige
 * PageSettingsPopover-Konsolidierung) — ein portaltes Menü würde dessen
 * Outside-Click-Handler auslösen und den Popover mitschließen (M5-Lehre).
 *
 * Props:
 *   options   — [{ value, label, icon? } | { divider: true, key }] — ein Trenner-Eintrag zeichnet eine Linie zwischen Gruppen
 *   value     — aktuell gewählter Wert
 *   onChange  — (value) => void
 *   placeholder — Text ohne Auswahl (default 'Wählen…')
 *   size      — 'md' (Standard) | 'sm' (kompakt)
 *   invalid   — boolean → rote Border
 *   disabled  — boolean
 *   portal    — boolean, default true; false rendert das Menü ohne Portal (siehe oben)
 */
import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Check } from 'lucide-react';
import './menu.css';
import './Select.css';

const Select = React.forwardRef(function Select(
  { value, onChange, options = [], placeholder = 'Wählen…', size = 'md', invalid = false, disabled = false, portal = true, className = '', ...rest },
  ref
) {
  const selected = options.find((o) => !o.divider && o.value === value);
  const classes = ['ui-select'];
  if (size === 'sm') classes.push('ui-select--sm');
  if (invalid) classes.push('ui-select--invalid');
  if (className) classes.push(className);

  const content = (
    <DropdownMenu.Content className="ui-menu" sideOffset={4} align="start">
      <DropdownMenu.RadioGroup value={value} onValueChange={(v) => onChange?.(v)}>
        {options.map((opt) => (
          opt.divider ? (
            <DropdownMenu.Separator key={opt.key ?? 'divider'} className="ui-menu__sep" />
          ) : (
            <DropdownMenu.RadioItem key={opt.value} className="ui-menu__item" value={opt.value}>
              <span className="ui-menu__check">
                <DropdownMenu.ItemIndicator>
                  <Check size={14} aria-hidden />
                </DropdownMenu.ItemIndicator>
              </span>
              {opt.icon && <opt.icon size={14} aria-hidden className="ui-menu__icon" />}
              {opt.label}
            </DropdownMenu.RadioItem>
          )
        ))}
      </DropdownMenu.RadioGroup>
    </DropdownMenu.Content>
  );

  return (
    <DropdownMenu.Root modal={portal}>
      <DropdownMenu.Trigger asChild>
        <button type="button" ref={ref} className={classes.join(' ')} disabled={disabled} {...rest}>
          <span className={`ui-select__value${selected ? '' : ' ui-select__value--placeholder'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown size={14} aria-hidden className="ui-select__caret" />
        </button>
      </DropdownMenu.Trigger>
      {portal ? <DropdownMenu.Portal>{content}</DropdownMenu.Portal> : content}
    </DropdownMenu.Root>
  );
});

export default Select;
